import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLudopedia } from '../hooks/useLudopedia';
import SEO from './SEO';
import FeedComponent from './feed/FeedComponent';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Settings, User, Mail, LogOut, Link as LinkIcon, BookOpenCheck, History, Award, Gift, Copy, Share2, CheckCircle } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';

const UserProfilePage: React.FC = () => {
  const { user, linkGoogleAccount, getUserIdentities, generateReferralCode } = useAuth();
  const { 
    loading, 
    ludopediaUser, 
    isConnected, 
    error, 
    connect, 
    disconnect, 
    connectWithAccessToken,
    importCollection 
  } = useLudopedia();
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<{ total: number; imported: number; errors: number } | null>(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [isConnectingManually, setIsConnectingManually] = useState(false);
  // Estado para mostrar/esconder o aviso sobre a página em branco
  const [showOAuthWarning, setShowOAuthWarning] = useState(true);
  // Estado para controlar o carregamento durante a vinculação com o Google
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  // Estado para armazenar informações sobre as identidades vinculadas
  const [linkedIdentities, setLinkedIdentities] = useState<any[]>([]);
  const [loadingIdentities, setLoadingIdentities] = useState(true);
  const [identityError, setIdentityError] = useState<string | null>(null);

  // Estados para o código de referência
  const [localReferralCode, setLocalReferralCode] = useState<string | null | undefined>(user?.referral_code);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Manter localReferralCode sincronizado com user.referral_code
  useEffect(() => {
    setLocalReferralCode(user?.referral_code);
  }, [user?.referral_code]);

  // Carregar identidades vinculadas quando o componente montar
  useEffect(() => {
    async function loadUserIdentities() {
      if (!user) return;
      
      setLoadingIdentities(true);
      setIdentityError(null);
      
      try {
        const { success, identities, error } = await getUserIdentities();
        
        if (success && identities) {
          setLinkedIdentities(identities);
        } else if (error) {
          setIdentityError(error);
        }
      } catch (error) {
        setIdentityError('Erro ao buscar identidades vinculadas');
        console.error('[UserProfile] Erro ao buscar identidades:', error);
      } finally {
        setLoadingIdentities(false);
      }
    }
    
    loadUserIdentities();
  }, [user, getUserIdentities]);

  // Verificar se já existe identidade do Google
  const hasGoogleIdentity = linkedIdentities.some(
    identity => identity.provider === 'google'
  );

  // Função para iniciar o fluxo OAuth da Ludopedia
  const handleConnectLudopedia = () => {
    setMessage(null);
    connect();
  };

  // Função para mostrar/esconder o formulário de token manual
  const toggleManualInput = () => {
    setShowManualInput(!showManualInput);
    setManualToken('');
    setMessage(null);
  };

  // Função para conectar usando token manual
  const handleManualConnect = async () => {
    if (!manualToken.trim()) {
      setMessage({ text: 'Por favor, insira um token válido', type: 'error' });
      return;
    }

    setIsConnectingManually(true);
    setMessage({ text: 'Verificando token...', type: 'info' });

    try {
      const result = await connectWithAccessToken(manualToken);
      
      if (result) {
        setMessage({ text: 'Conta da Ludopedia conectada com sucesso', type: 'success' });
        setShowManualInput(false);
        setManualToken('');
      } else if (error) {
        setMessage({ text: error, type: 'error' });
      } else {
        setMessage({ text: 'Não foi possível conectar com o token fornecido', type: 'error' });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      setMessage({ text: `Erro ao conectar: ${errorMsg}`, type: 'error' });
    } finally {
      setIsConnectingManually(false);
    }
  };

  // Função para desconectar conta da Ludopedia
  const handleDisconnectLudopedia = async () => {
    setMessage(null);
    
    const result = await disconnect();
    
    if (result) {
      setMessage({ text: 'Conta da Ludopedia desconectada com sucesso', type: 'success' });
      setImportResults(null);
    } else if (error) {
      setMessage({ text: error, type: 'error' });
    } else {
      setMessage({ text: 'Erro ao desconectar conta da Ludopedia', type: 'error' });
    }
  };

  // Função para importar coleção da Ludopedia
  const handleImportCollection = async () => {
    setIsImporting(true);
    setMessage({ text: 'Importando coleção da Ludopedia...', type: 'info' });
    setImportResults(null);

    try {
      const result = await importCollection();
      
      if (result.success && 'results' in result && result.results) {
        setImportResults(result.results);
        setMessage({ 
          text: `Importação concluída! ${result.results.imported} jogos importados com sucesso.`, 
          type: 'success' 
        });
      } else {
        setMessage({ text: result.error || 'Erro ao importar coleção da Ludopedia', type: 'error' });
      }
    } catch (error) {
      console.error('Erro ao importar coleção:', error);
      setMessage({ 
        text: error instanceof Error ? error.message : 'Erro ao importar coleção da Ludopedia', 
        type: 'error' 
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Função para vincular conta do Google
  const handleLinkGoogleAccount = async () => {
    setIsLinkingGoogle(true);
    setMessage(null);
    
    try {
      const { success, error } = await linkGoogleAccount();
      
      if (!success && error) {
        setMessage({ text: error, type: 'error' });
      } else {
        // Se foi bem-sucedido, o usuário será redirecionado para o fluxo OAuth do Google
        setMessage({ text: 'Você será redirecionado para o Google para vincular sua conta', type: 'info' });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      setMessage({ text: `Erro ao vincular conta Google: ${errorMsg}`, type: 'error' });
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  // <<< NOVO: Handler para clique em tag no feed do perfil >>>
  // Por enquanto, apenas loga. Pode ser expandido para filtrar ou navegar.
  const handleProfileFeedTagClick = (type: string, value: string) => {
    console.log(`[UserProfilePage] Clicou na tag: ${type} = ${value}. Nenhuma ação implementada.`);
    // Poderia navegar para a página de busca com o filtro, por exemplo:
    // navigate(`/search?tagType=${type}&tagValue=${value}`);
  };

  const handleGenerateReferralCode = async () => {
    if (!user) return;
    setIsGeneratingCode(true);
    setGenerateError(null);
    try {
      const result = await generateReferralCode();
      if (result.success && result.code) {
        setLocalReferralCode(result.code);
        toast.success('Seu código de indicação foi gerado!');
      } else {
        setGenerateError(result.error || 'Falha ao gerar o código.');
        toast.error(result.error || 'Falha ao gerar o código de indicação.');
      }
    } catch (error: any) {
      setGenerateError(error.message || 'Erro desconhecido ao gerar código.');
      toast.error(error.message || 'Erro desconhecido ao gerar código de indicação.');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleCopyReferralLink = () => {
    if (!localReferralCode) return;
    const referralLink = `${window.location.origin}/signup?ref=${localReferralCode}`;
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopied(true);
        toast.success('Link de indicação copiado!');
        setTimeout(() => setCopied(false), 2000); 
      })
      .catch(err => {
        console.error('Erro ao copiar link:', err);
        toast.error('Erro ao copiar o link.');
      });
  };

  return (
    <>
      <SEO 
        title={`Perfil do Usuário - ${user?.username || 'Boardverso'}`}
        description="Gerencie seu perfil, conecte contas externas e importe sua coleção de jogos de tabuleiro."
      />
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6">Perfil do Usuário</h1>
        
        {/* Seção de Código de Referência */}
        {user && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Gift className="mr-3 h-6 w-6 text-indigo-600" />
                Programa de Indicação
              </h2>
              {localReferralCode && (
                <span className="text-xs mt-2 sm:mt-0 px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                  Você já tem um código!
                </span>
              )}
            </div>

            {localReferralCode ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Compartilhe seu código de indicação com amigos! Quando eles se cadastrarem usando seu código, vocês dois ganham recompensas.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3 p-3 border border-dashed border-indigo-300 rounded-lg bg-indigo-50">
                  <span className="text-lg font-mono text-indigo-700 tracking-wider select-all">
                    {localReferralCode}
                  </span>
                  <Button 
                    onClick={handleCopyReferralLink}
                    variant="outline"
                    size="sm"
                    className="bg-white border-indigo-600 text-indigo-600 hover:bg-indigo-100 w-full sm:w-auto"
                  >
                    {copied ? <CheckCircle className="mr-2 h-4 w-4 text-green-500" /> : <Copy className="mr-2 h-4 w-4" />}
                    {copied ? 'Copiado!' : 'Copiar Link'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Seu link de indicação é: <code className="bg-gray-100 p-1 rounded text-xs">{`${window.location.origin}/signup?ref=${localReferralCode}`}</code>
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Você ainda não gerou seu código de indicação. Gere seu código para começar a indicar amigos e ganhar recompensas!
                </p>
                <Button 
                  onClick={handleGenerateReferralCode} 
                  disabled={isGeneratingCode}
                  className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {isGeneratingCode ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Gerando Código...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Gift className="mr-2 h-4 w-4" /> Gerar Meu Código de Indicação
                    </span>
                  )}
                </Button>
                {generateError && (
                  <p className="text-sm text-red-600">{generateError}</p>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Informações básicas do usuário */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Suas Informações</h2>
          
          {user ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <User className="text-gray-500" />
                <span>{user.username || 'Nome de usuário não definido'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="text-gray-500" />
                <span>{user.email}</span>
              </div>
              
              {/* Botão para Badges (deixar comentado por enquanto) */}
              {/*
              <Button variant="outline" size="sm" asChild className="mt-2 ml-2">
                 <Link to="/profile/badges">
                    <Award className="mr-2 h-4 w-4" /> Ver Badges
                 </Link>
              </Button>
              */}

            </div>
          ) : (
            <p>Carregando informações do usuário...</p>
          )}
        </div>
        
        {/* Seção de Vinculação com o Google */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Contas Vinculadas</h2>
          
          {loadingIdentities ? (
            <div className="flex items-center justify-center py-4">
              <svg className="animate-spin h-5 w-5 text-gray-500 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Carregando identidades vinculadas...</span>
            </div>
          ) : identityError ? (
            <div className="p-4 bg-red-100 text-red-800 rounded mb-4">
              {identityError}
            </div>
          ) : (
            <div>
              <h3 className="font-medium mb-3">Google</h3>
              {hasGoogleIdentity ? (
                <div className="flex items-center mb-4 p-3 border border-gray-200 rounded bg-gray-50">
                  <svg className="w-5 h-5 mr-3 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium">Conta Google vinculada</p>
                    <p className="text-sm text-gray-600">Você pode fazer login usando sua conta Google</p>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Vincule sua conta do Google para poder acessar com seu e-mail do Google. Você continuará também 
                    podendo acessar com seu e-mail e senha originais.
                  </p>
                  
                  <button
                    onClick={handleLinkGoogleAccount}
                    disabled={isLinkingGoogle}
                    className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 font-medium py-2 px-4 rounded inline-flex items-center disabled:opacity-50"
                  >
                    {isLinkingGoogle ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processando...
                      </span>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        Vincular Conta Google
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {message && (
                <div className={`p-3 mt-3 rounded ${
                  message.type === 'success' ? 'bg-green-100 text-green-800' : 
                  message.type === 'error' ? 'bg-red-100 text-red-800' : 
                  'bg-blue-100 text-blue-800'
                }`}>
                  {message.text}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Integração com Ludopedia */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Integração com Ludopedia</h2>
          
          {isConnected && ludopediaUser ? (
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                {ludopediaUser.avatar_url && (
                  <img 
                    src={ludopediaUser.avatar_url} 
                    alt={`Avatar de ${ludopediaUser.username}`} 
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <p className="font-medium text-lg">{ludopediaUser.name || ludopediaUser.username}</p>
                  <p className="text-sm text-gray-600">@{ludopediaUser.username}</p>
                </div>
              </div>
              
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  onClick={handleDisconnectLudopedia}
                  disabled={loading}
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                >
                  {loading ? 'Processando...' : 'Desconectar conta da Ludopedia'}
                </button>
                
                <button
                  onClick={handleImportCollection}
                  disabled={loading || isImporting}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                >
                  {isImporting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Importando...
                    </span>
                  ) : 'Importar coleção da Ludopedia'}
                </button>
              </div>
              
              {importResults && (
                <div className="bg-gray-100 p-4 rounded mt-4">
                  <h3 className="font-medium mb-2">Resultados da importação:</h3>
                  <p>Total de jogos: {importResults.total}</p>
                  <p>Jogos importados: {importResults.imported}</p>
                  <p>Erros: {importResults.errors}</p>
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* Aviso sobre o problema da página em branco */}
              {!isConnected && showOAuthWarning && (
                <div className="mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                  <div className="flex justify-between">
                    <h3 className="font-semibold text-yellow-800">⚠️ Problema conhecido</h3>
                    <button 
                      onClick={() => setShowOAuthWarning(false)}
                      className="text-yellow-800 hover:text-yellow-900"
                    >
                      ✕
                    </button>
                  </div>
                  <p className="mt-2 text-sm">
                    Identificamos um problema na tela de autorização da Ludopedia que pode aparecer em branco. 
                    Se isso acontecer, recomendamos usar a opção "Inserir token manualmente" abaixo.
                  </p>
                  <p className="mt-2 text-sm">
                    Para obter seu token de acesso, entre em contato com o suporte da Ludopedia.
                  </p>
                </div>
              )}
            
              <p className="mb-4">
                Conecte sua conta da Ludopedia para importar sua coleção de jogos automaticamente.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                {/* Opção 1: OAuth (com aviso sobre possível problema) */}
                <div className="border rounded p-4 bg-white">
                  <h3 className="font-semibold mb-2">Opção 1: Conexão com Ludopedia</h3>
                  <p className="text-sm mb-4">
                    Você será redirecionado para a Ludopedia para autorizar o acesso.
                  </p>
                  <button
                    onClick={handleConnectLudopedia}
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 w-full"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processando...
                      </span>
                    ) : 'Conectar com OAuth'}
                  </button>
                </div>
                
                {/* Opção 2: Entrada manual de token (recomendada atualmente) */}
                <div className="border rounded p-4 bg-white border-green-300 shadow-md">
                  <div className="flex items-start mb-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full mr-2">
                      Recomendado
                    </span>
                    <h3 className="font-semibold">Opção 2: Inserir token manualmente</h3>
                  </div>
                  
                  <p className="text-sm mb-4">
                    Se você já possui um token de acesso da Ludopedia, insira-o diretamente aqui.
                  </p>
                  
                  <div className="flex flex-col gap-2">
                    <input
                      type="text"
                      value={manualToken}
                      onChange={(e) => setManualToken(e.target.value)}
                      placeholder="Insira seu token de acesso"
                      className="border rounded px-3 py-2 w-full"
                    />
                    
                    <button
                      onClick={handleManualConnect}
                      disabled={isConnectingManually || !manualToken.trim()}
                      className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 w-full"
                    >
                      {isConnectingManually ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Conectando...
                        </span>
                      ) : 'Conectar com token'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Para obter um token de acesso, entre em contato com o suporte da Ludopedia.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {message && (
            <div className={`mt-4 p-3 rounded ${
              message.type === 'success' ? 'bg-green-100 text-green-800' : 
              message.type === 'error' ? 'bg-red-100 text-red-800' : 
              'bg-blue-100 text-blue-800'
            }`}>
              {message.text}
            </div>
          )}
          
          {/* Mostrar erro do hook se não houver uma mensagem já definida */}
          {error && !message && (
            <div className="mt-4 p-3 rounded bg-red-100 text-red-800">
              {error}
            </div>
          )}
        </div>

        {/* Seção de Gerenciamento de Conta */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Gerenciamento de Conta</h2>
          
          <div className="space-y-3">
            <Link 
              to="/alterar-senha" 
              className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50"
            >
              <div className="flex items-center">
                <span className="font-medium">Alterar senha</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Link>

            <Link 
              to="/configuracoes-privacidade" 
              className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50"
            >
              <div className="flex items-center">
                <span className="font-medium">Configurações de privacidade</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Link>
            
            <Link 
              to="/excluir-conta" 
              className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50 text-red-600"
            >
              <div className="flex items-center">
                <span className="font-medium">Excluir minha conta</span>
              </div>
              <ChevronRight className="h-5 w-5 text-red-400" />
            </Link>
          </div>
        </div>

        {/* <<< BOTÃO INSERIDO AQUI >>> */}
        {user && (
          <div className="mb-6"> {/* Div para espaçamento */}
              <Button variant="outline" size="sm" asChild>
                 <Link to="/profile/xp-history">
                    <History className="mr-2 h-4 w-4" /> Ver Histórico de XP
                 </Link>
              </Button>
               {/* Botão para Badges (deixar comentado por enquanto) */}
              {/*
              <Button variant="outline" size="sm" asChild className="ml-2">
                 <Link to="/profile/badges">
                    <Award className="mr-2 h-4 w-4" /> Ver Badges
                 </Link>
              </Button>
              */}
          </div>
        )}

        {/* <<< NOVA SEÇÃO: Atividade Recente (Feed do Usuário) >>> */}
        {user && (
          <>
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Sua Atividade Recente</h2>
                {/* Link opcional para uma página de feed completa */}
                <Link 
                  to={`/feed?userId=${user.id}`} 
                  className="text-sm font-medium text-primary hover:underline flex items-center"
                >
                  Ver Feed Completo <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm">
                {/* Adiciona verificação explícita aqui também, embora redundante */}
                {user && (
                  <FeedComponent 
                      key={user.id}
                      externalFilters={{ filterUserId: user.id }} 
                      limit={10} 
                      showCreatePostForm={false}
                      onTagClick={handleProfileFeedTagClick}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default UserProfilePage; 